/**
 * ARI Cloud Entry Point
 *
 * Uses Auth0 for authentication. Backend handles all Auth0 login flows via
 * auth0-fastapi, using HTTP-only session cookies.
 */
import '@fontsource-variable/inter';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { createQueryClient } from '@nexus/ari-lib/api/query-client';
import { configureChatsApi } from '@nexus/ari-lib/api/chats';
import { configureUserApi } from '@nexus/ari-lib/api/user';
import { initSupabase, initAuth0, getAccessToken } from '@nexus/supabase';
import { QueryClientProvider } from '@tanstack/react-query';

import App from './app/app';
import { createAppRouter } from './routes';
import { DocumentsUploadsProvider } from './sections/documents';
import { setupLocaleSync } from './hooks/use-locale-sync';
import { getSupabaseConfig, getSupabaseUrl, getAuthBackendUrl } from './config/global';

// Bridge i18n languageChanged into the store once at app boot — survives any
// component lifecycle.
setupLocaleSync();

async function initializeApp() {
  const supabaseConfig = getSupabaseConfig();

  initAuth0({ backendUrl: getAuthBackendUrl() });

  initSupabase({
    url: supabaseConfig.url,
    anonKey: supabaseConfig.anonKey,
    accessToken: async () => {
      try {
        return await getAccessToken();
      } catch {
        return '';
      }
    },
  });

  configureChatsApi(getSupabaseUrl());
  configureUserApi(getSupabaseUrl());
}

const queryClient = createQueryClient();

// Router is built with the queryClient in context so route loaders can
// prefetch via queryClient.ensureQueryData(...).
const router = createAppRouter(queryClient);

// Initialize and render
initializeApp().then(() => {
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <DocumentsUploadsProvider>
          <App router={router} />
        </DocumentsUploadsProvider>
      </QueryClientProvider>
    </StrictMode>
  );
});
